import { StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";

// <Avatar> — initials-in-a-circle. Net-new primitive (grep: no avatar component
// existed anywhere). JL has no uploaded photos, so identity reads from the
// display name's initials. `tone` lets a caller echo a row's priority (a
// flagged athlete's avatar goes danger/warn) — always paired with the row's own
// tags/label, so the tint never carries state alone (a11y). RN-portable, no img.

export type AvatarTone = "neutral" | "warn" | "danger";

export type AvatarProps = {
  theme: UiTheme;
  name: string;
  size?: number;
  tone?: AvatarTone;
};

// First letter of the first + last whitespace-separated word, uppercased. One
// word → its first letter; empty → "?". Kept pure so the dashboard test can pin
// the mapping.
export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]![0]!;
  const last = words.length > 1 ? words[words.length - 1]![0]! : "";
  return (first + last).toUpperCase();
}

export function Avatar({ theme, name, size = 40, tone = "neutral" }: AvatarProps) {
  const styles = makeStyles(theme);
  return (
    <View
      style={[
        styles.wrap,
        styles[`bg_${tone}`],
        { width: size, height: size, borderRadius: size / 2 },
      ]}
    >
      <Text
        style={[styles.initials, styles[`fg_${tone}`], { fontSize: size * 0.34 }]}
        numberOfLines={1}
      >
        {initialsFor(name)}
      </Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p } = theme;
  return StyleSheet.create({
    wrap: { alignItems: "center", justifyContent: "center" },
    initials: { fontWeight: "700", fontVariant: ["tabular-nums"] },
    bg_neutral: { backgroundColor: p.surface2 },
    fg_neutral: { color: p.ink2 },
    bg_warn: { backgroundColor: p.warn },
    fg_warn: { color: p.inkOnWarn },
    bg_danger: { backgroundColor: p.danger },
    fg_danger: { color: p.inkOnDanger },
  });
}
